import { formatRelative } from './format.js';

export const ATTENTION_FILTER_OPTIONS = [
  { label: 'All applicants', params: {} },
  { label: 'Needs attention', params: { attention: '1' } },
  { label: 'Text failed', params: { attention: '1', attention_reason: 'failed_text' } },
  { label: 'Needs interview', params: { attention: '1', attention_reason: 'reply_needs_scheduling' } },
  { label: 'No-show follow-up', params: { attention: '1', attention_reason: 'no_show_followup' } },
  { label: 'Stale no-response', params: { attention: '1', attention_reason: 'stale_no_response' } },
  { label: 'Missing phone', params: { attention: '1', attention_reason: 'missing_phone' } },
];

export const CAMPAIGN_FILTER_OPTIONS = [
  { label: 'Any campaign status', value: '' },
  { label: 'Active campaign', value: 'active' },
  { label: 'Queued campaign', value: 'queued' },
  { label: 'Skipped campaign', value: 'skipped' },
  { label: 'Failed campaign', value: 'failed' },
  { label: 'Campaign complete', value: 'completed' },
];

export const NEXT_ACTION_FILTER_OPTIONS = [
  { label: 'Any next action', value: '' },
  { label: 'Schedule interview', value: 'schedule_interview' },
  { label: 'Mark interview outcome', value: 'mark_interview_outcome' },
  { label: 'Review failed text', value: 'review_failed_text' },
  { label: 'Add phone', value: 'add_phone' },
  { label: 'Waiting for reply', value: 'wait_for_reply' },
];

const CAMPAIGN_VARIANTS = {
  queued: 'info',
  active: 'success',
  paused: 'warning',
  completed: 'neutral',
  skipped: 'warning',
  failed: 'danger',
};

const NEXT_ACTION_LABELS = {
  wait_for_reply: 'Waiting for reply',
  send_first_contact: 'Start first contact',
  schedule_interview: 'Schedule interview',
  mark_interview_outcome: 'Mark interview outcome',
  review_failed_text: 'Review failed text',
  add_phone: 'Add phone',
  follow_up_no_show: 'Follow up no-show',
  review_stale_no_response: 'Review stale applicant',
  none: 'No action',
};

const NEXT_ACTION_TONE_CLASSES = {
  blue: 'text-brand-blue',
  amber: 'text-warning-text',
  red: 'text-danger-text',
  green: 'text-success-text',
  gray: 'text-text-muted',
  neutral: 'text-text-secondary',
};

export function getAttentionFilterValue({ attention, attentionReason } = {}) {
  if (attention === '1' && attentionReason) return attentionReason;
  if (attention === '1') return 'attention';
  return '';
}

export function getAttentionFilterParams(value) {
  if (!value) return { attention: '', attention_reason: '' };
  if (value === 'attention') return { attention: '1', attention_reason: '' };
  return { attention: '1', attention_reason: value };
}

export function getWorkflowStageLabel(applicant = {}) {
  return applicant.workflow?.stageLabel || applicant.workflow?.currentStageLabel || null;
}

export function getCampaignBadge(workflow = {}) {
  const status = workflow.campaignStatus || workflow.campaign?.status || 'not_started';
  if (status === 'not_started') return null;
  return {
    status,
    label: workflow.campaignLabel || workflow.campaign?.label || 'Campaign status',
    variant: CAMPAIGN_VARIANTS[status] || 'neutral',
  };
}

export function getWorkflowNextAction(applicant = {}) {
  const workflow = applicant.workflow || {};
  const key = workflow.nextActionKey || workflow.nextAction?.key || null;
  const label = workflow.nextActionLabel || workflow.nextAction?.title || NEXT_ACTION_LABELS[key] || 'Review';
  const tone = workflow.nextActionTone || workflow.nextAction?.tone || 'blue';
  return {
    key,
    label: NEXT_ACTION_LABELS[key] || label,
    className: NEXT_ACTION_TONE_CLASSES[tone] || NEXT_ACTION_TONE_CLASSES.blue,
  };
}

export function getWorkflowLastTouch(workflow = {}, fallbackLastContact = null) {
  if (workflow.lastInboundAt) return `Reply ${formatRelative(workflow.lastInboundAt)}`;
  if (workflow.lastOutboundAt) return `Text ${formatRelative(workflow.lastOutboundAt)}`;
  if (workflow.lastContactAt || fallbackLastContact) return formatRelative(workflow.lastContactAt || fallbackLastContact);
  return 'No contact';
}

export function getWorkflowInterviewSummary(workflow = {}) {
  if (workflow.nextInterviewAt) return formatRelative(workflow.nextInterviewAt);
  if (workflow.lastInterviewStatus === 'no_show') return 'No-show';
  if (workflow.lastInterviewStatus === 'completed') return 'Completed';
  if (workflow.lastInterviewStatus === 'cancelled') return 'Cancelled';
  return '—';
}

export function getWorkflowEmptyState({ attention, attentionReason, campaignStatus } = {}) {
  if (attention === '1' || attentionReason) return {
    title: 'No applicants need attention right now.',
    description: 'Automation is running normally for this filter.',
  };
  if (campaignStatus) return {
    title: 'No applicants match this campaign status.',
    description: 'Try another campaign filter or clear filters.',
  };
  return {
    title: 'No applicants found',
    description: 'Import a CSV or share the application form to start the hiring queue.',
  };
}
